import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

/**
 * How a SKU's cost reads on Product Detail (the Cost cell) and in Edit SKU
 * (the hints under the Default cost input). A SKU's cost is its average cost
 * (WAC) at a location, else its Default cost; nothing else is ever shown.
 *
 * Components only (react-refresh/only-export-components).
 */

// Same format as Product Detail's other cost cells. The hard-coded `$` is a
// known follow-up (it ignores the org currency).
function formatCost(value: number | null): string {
  if (value == null) return '-';
  return `$${value.toFixed(2)}`;
}

interface VariantCostCellProps {
  sku: string;
  locations: ReadonlyArray<{ weightedAverageCost: number | null }>;
  skuCostOverride: number | null;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

/** Product Detail's Cost cell: WAC (one location, or "Varies By Location"), else the Default cost. */
export function VariantCostCell({ sku, locations, skuCostOverride, isExpanded, onToggleExpand }: VariantCostCellProps) {
  const { t } = useTranslation('inventory');
  const hasWac = locations.some(l => l.weightedAverageCost != null);
  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex items-center gap-1.5">
        {locations.length > 0 && (
          <button
            onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}
            className="p-1 -m-1 hover:bg-muted rounded transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
            aria-expanded={isExpanded}
            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} location details for ${sku}`}
          >
            <ChevronRight className={cn(
              'h-3.5 w-3.5 text-muted-foreground transition-transform duration-200',
              isExpanded && 'rotate-90'
            )} />
          </button>
        )}
        {hasWac && locations.length === 1 ? (
          <span className="font-medium tabular-nums">{formatCost(locations[0]?.weightedAverageCost ?? null)}</span>
        ) : hasWac ? (
          <span className="text-muted-foreground text-sm cursor-pointer" onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}>Varies By Location</span>
        ) : (
          <span className="tabular-nums text-muted-foreground">
            {formatCost(skuCostOverride)}
          </span>
        )}
      </div>
      {/* Stock value: average cost, else the Default cost. */}
      {hasWac ? (
        <Badge variant="success" size="sm">{t('productDetail.costSourceAvg')}</Badge>
      ) : skuCostOverride != null ? (
        <Badge variant="muted" size="sm">{t('productDetail.costSourceDefault')}</Badge>
      ) : null}
    </div>
  );
}

interface SkuCostHintsProps {
  /** The org currency code. */
  currency: string;
  /** The Default cost input as typed. */
  inputValue: string;
  /** The saved Default cost (`sku.skuCostOverride`). */
  savedOverride: number | null;
  /** Whether the SKU has a WAC anywhere (from the opening page's rows). */
  hasWac: boolean;
  /** Locations with a WAC, from the per-location breakdown. */
  wacLocationCount: number;
  locationCount: number;
}

/** Edit SKU: the hints under the Default cost input. */
export function SkuCostHints({ currency, inputValue, savedOverride, hasWac, wacLocationCount, locationCount }: SkuCostHintsProps) {
  const { t } = useTranslation('inventory');
  return (
    <div className="text-xs text-muted-foreground space-y-1">
      <p>{t('editSkuModal.costHelp', { currency })}</p>
      {wacLocationCount > 0 && locationCount > 0 ? (
        <p className="text-emerald-600 font-medium">
          {t('editSkuModal.avgCostAtLocations', { count: locationCount, wacCount: wacLocationCount })}
          {wacLocationCount < locationCount && savedOverride != null && (
            <> {t('editSkuModal.otherLocationsUseDefault', { cost: `${savedOverride.toFixed(2)} ${currency}` })}</>
          )}
        </p>
      ) : hasWac ? (
        <p className="text-emerald-600 font-medium">{t('editSkuModal.hasAvgCost')}</p>
      ) : null}
      {!inputValue && wacLocationCount === 0 && !hasWac && (
        <p className="text-amber-600">{t('editSkuModal.noCost')}</p>
      )}
    </div>
  );
}
